import React from 'react';
import {
  Circle,
  ClipPath,
  Defs,
  Document,
  G,
  Image,
  Page,
  pdf,
  StyleSheet,
  Svg,
  Line as SvgLine,
  Text,
  View,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import type { GraphData } from './exportUtils';
import type { LegendItem } from './captureGraphs';
import { PdfCoverPage } from './pdfCoverPage/PdfCoverPage';
import './marketTrendsPdfUtils/pdfStyles';
import zillowRentalsLogo from '../img/ZillowRentals-brand-light.png';

export type TextSection = {
  title: string;
  items: Array<{ label: string; value: string; change?: string }>;
};

export type PdfGraphPage = {
  name: string;
  imageData: string;
  tableData?: GraphData;
  legend?: Array<LegendItem>;
  description?: string;
};

export type CoverPageData = {
  title: string;
  propertyName: string | null;
  propertyImageUrl?: string;
  date: string;
  filters: Array<{ label: string; value: string }>;
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    paddingBottom: 50,
    fontSize: 10,
    fontFamily: 'OpenSans',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerRight: {
    fontFamily: 'OpenSans',
    fontSize: 10,
    color: '#6B7280',
  },
  contentCard: {
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  graphTitle: {
    fontFamily: 'ObjectSans',
    fontSize: 13,
    color: '#111827',
    marginBottom: 4,
  },
  graphDescription: {
    fontFamily: 'OpenSans',
    fontSize: 10,
    color: '#6B7280',
    marginBottom: 16,
    marginTop: 8,
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    maxWidth: '100%',
    objectFit: 'contain',
  },
  table: {
    marginTop: 8,
  },
  tableHeaderRow: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '1px solid #E5E7EB',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    borderBottom: '1px solid #F3F4F6',
  },
  tableHeaderCell: {
    fontFamily: 'OpenSansBold',
    fontSize: 8,
    color: '#6B7280',
    paddingVertical: 5,
    paddingHorizontal: 3,
    textAlign: 'right',
  },
  tableCell: {
    fontFamily: 'OpenSans',
    fontSize: 8,
    color: '#374151',
    paddingVertical: 5,
    paddingHorizontal: 3,
    textAlign: 'right',
  },
  tableRowLabelCell: {
    fontFamily: 'OpenSansBold',
    fontSize: 8,
    color: '#374151',
    paddingVertical: 5,
    paddingHorizontal: 3,
    textAlign: 'left',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerSource: {
    fontSize: 8,
    color: '#6B7280',
  },
  footerSourceBold: {
    fontFamily: 'OpenSansBold',
    fontSize: 8,
    color: '#374151',
  },
  footerPage: {
    fontSize: 8,
    color: '#6B7280',
  },
  textContent: {
    flex: 1,
    marginTop: 20,
  },
  textItem: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: '1px solid #e0e0e0',
  },
  textItemLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 5,
  },
  textItemValue: {
    fontSize: 16,
    fontFamily: 'OpenSansBold',
    color: '#000',
  },
  textItemChange: {
    fontSize: 10,
    color: '#666',
    marginTop: 3,
  },
});

const AVAILABLE_TABLE_WIDTH = 782;
const TABLE_LABEL_WIDTH = 110;

const buildMtdMapping = (columns: Array<string>): Record<string, string> => {
  const mtdColumns = columns.filter((col) => col.startsWith('MTD '));
  const nonMtdColumns = columns.filter((col) => !col.startsWith('MTD ') && col !== columns[0]);
  const mapping: Record<string, string> = {};

  for (const mtdCol of mtdColumns) {
    const baseName = mtdCol.replace(/^MTD /, '');
    const match = nonMtdColumns.find((col) => col === baseName || col.endsWith(baseName));
    if (match) {
      mapping[match] = mtdCol;
    }
  }

  return mapping;
};

const DataTable = ({ tableData }: { tableData: GraphData }) => {
  const { data, columns } = tableData;
  const monthKey = columns[0];
  const mtdMapping = buildMtdMapping(columns);
  const metricKeys = columns.slice(1).filter((col) => !col.startsWith('MTD '));
  const lastIndex = data.length - 1;

  const monthLabels = data.map((row) => String(row[monthKey] ?? ''));

  const cellWidth = Math.max(30, (AVAILABLE_TABLE_WIDTH - TABLE_LABEL_WIDTH) / monthLabels.length);

  return (
    <View style={styles.table}>
      <View style={styles.tableHeaderRow}>
        <Text style={[styles.tableHeaderCell, { width: TABLE_LABEL_WIDTH, textAlign: 'left' }]}>{monthKey}</Text>
        {monthLabels.map((month, i) => (
          <Text key={i} style={[styles.tableHeaderCell, { width: cellWidth }]}>
            {month}
          </Text>
        ))}
      </View>
      {metricKeys.map((metric, metricIndex) => (
        <View key={metricIndex} style={styles.tableRow}>
          <Text style={[styles.tableRowLabelCell, { width: TABLE_LABEL_WIDTH }]}>{metric}</Text>
          {data.map((row, monthIndex) => {
            const mtdCol = mtdMapping[metric];
            const value = monthIndex === lastIndex && mtdCol ? row[mtdCol] : row[metric];
            const isPercent = metric.includes('(%)') || metric.toLowerCase().includes('vacancy rate');
            const formatted = value != null ? (isPercent ? `${value}%` : String(value)) : '-';
            return (
              <Text key={monthIndex} style={[styles.tableCell, { width: cellWidth }]}>
                {formatted}
              </Text>
            );
          })}
        </View>
      ))}
    </View>
  );
};

const LegendAdornment = ({ item }: { item: LegendItem }) => {
  switch (item.type) {
    case 'line':
      return <View style={{ width: 16, height: 3, backgroundColor: item.color, borderRadius: 1 }} />;
    case 'dashed-line':
      return (
        <View style={{ width: 16, height: 3, display: 'flex', flexDirection: 'row', gap: 2 }}>
          <View style={{ width: 5, height: 3, backgroundColor: item.color, borderRadius: 1 }} />
          <View style={{ width: 5, height: 3, backgroundColor: item.color, borderRadius: 1 }} />
        </View>
      );
    case 'striped': {
      const light = item.stripeColors?.light ?? '#B88DFC';
      const dark = item.stripeColors?.dark ?? '#891EFA';
      const clipId = `stripe-clip-${item.label.replace(/\s+/g, '-')}`;
      return (
        <Svg width={12} height={12} viewBox="0 0 12 12">
          <Defs>
            <ClipPath id={clipId}>
              <Circle cx="6" cy="6" r="6" />
            </ClipPath>
          </Defs>
          <G clipPath={`url(#${clipId})`}>
            <Circle cx="6" cy="6" r="6" fill={light} />
            <SvgLine x1="3" y1="0" x2="0" y2="3" stroke={dark} strokeWidth={1.5} />
            <SvgLine x1="6" y1="0" x2="0" y2="6" stroke={dark} strokeWidth={1.5} />
            <SvgLine x1="9" y1="0" x2="0" y2="9" stroke={dark} strokeWidth={1.5} />
            <SvgLine x1="12" y1="0" x2="0" y2="12" stroke={dark} strokeWidth={1.5} />
            <SvgLine x1="12" y1="3" x2="3" y2="12" stroke={dark} strokeWidth={1.5} />
            <SvgLine x1="12" y1="6" x2="6" y2="12" stroke={dark} strokeWidth={1.5} />
            <SvgLine x1="12" y1="9" x2="9" y2="12" stroke={dark} strokeWidth={1.5} />
          </G>
        </Svg>
      );
    }
    default:
      return <View style={{ width: 10, height: 10, backgroundColor: item.color, borderRadius: 5 }} />;
  }
};

const GraphLegend = ({ items }: { items: Array<LegendItem> }) => (
  <View
    style={{
      display: 'flex',
      flexDirection: 'row',
      gap: 16,
      marginTop: 10,
      justifyContent: 'center',
      flexWrap: 'wrap',
    }}
  >
    {items.map((item, i) => (
      <View key={i} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <LegendAdornment item={item} />
        <Text style={{ fontFamily: 'OpenSans', fontSize: 8, color: '#374151' }}>{item.label}</Text>
      </View>
    ))}
  </View>
);

const PageHeader = ({ title, date }: { title: string; date: string }) => (
  <View style={styles.header}>
    <Image src={zillowRentalsLogo.src} style={{ width: 130, height: 15, objectFit: 'contain' }} />
    <Text style={styles.headerRight}>
      {title} · {date}
    </Text>
  </View>
);

const PageFooter = ({ pageNumber }: { pageNumber: number }) => (
  <View style={styles.footer}>
    <Text style={styles.footerSource}>
      <Text style={styles.footerSourceBold}>Source</Text> Zillow internal data
    </Text>
    <Text style={styles.footerPage}>{pageNumber}</Text>
  </View>
);

type PDFDocumentProps = {
  tabName: string;
  graphs: Array<PdfGraphPage>;
  textSections: Array<TextSection>;
  coverPageData?: CoverPageData;
};

const PDFDocument = ({ tabName, graphs, textSections, coverPageData }: PDFDocumentProps) => {
  const hasCoverPage = !!coverPageData;
  const pageOffset = hasCoverPage ? 1 : 0;
  const date = coverPageData?.date ?? format(new Date(), 'MMMM d, yyyy');
  const title = coverPageData?.title ?? tabName;

  return (
    <Document>
      {coverPageData && (
        <PdfCoverPage
          title={coverPageData.title}
          propertyName={coverPageData.propertyName}
          propertyImageUrl={coverPageData.propertyImageUrl}
          date={coverPageData.date}
          filters={coverPageData.filters}
        />
      )}
      {textSections.map((section, sectionIndex) => {
        const pageNum = sectionIndex + 1 + pageOffset;
        return (
          <Page key={`text-${sectionIndex}`} size="LEGAL" orientation="landscape" style={styles.page}>
            <PageHeader title={title} date={date} />
            <View style={styles.contentCard}>
              <Text style={styles.graphTitle}>{section.title}</Text>
              <View style={styles.textContent}>
                {section.items.map((item, itemIndex) => (
                  <View key={itemIndex} style={styles.textItem}>
                    <Text style={styles.textItemLabel}>{item.label}</Text>
                    <Text style={styles.textItemValue}>{item.value}</Text>
                    {item.change && <Text style={styles.textItemChange}>Change: {item.change}</Text>}
                  </View>
                ))}
              </View>
            </View>
            <PageFooter pageNumber={pageNum} />
          </Page>
        );
      })}
      {graphs.map((graph, graphIndex) => {
        const pageNum = textSections.length + graphIndex + 1 + pageOffset;
        return (
          <Page key={`graph-${graphIndex}`} size="LEGAL" orientation="landscape" style={styles.page}>
            <PageHeader title={title} date={date} />
            <View style={styles.contentCard}>
              <Text style={styles.graphTitle}>{graph.name}</Text>
              {graph.description ? <Text style={styles.graphDescription}>{graph.description}</Text> : null}
              <View style={styles.imageContainer}>
                <Image src={graph.imageData} style={styles.image} />
              </View>
              {graph.legend && graph.legend.length > 0 && <GraphLegend items={graph.legend} />}
              {graph.tableData && <DataTable tableData={graph.tableData} />}
            </View>
            <PageFooter pageNumber={pageNum} />
          </Page>
        );
      })}
    </Document>
  );
};

export const downloadPDF = async (
  graphs: Array<PdfGraphPage>,
  tabName: string,
  textSections: Array<TextSection> = [],
  coverPageData?: CoverPageData,
) => {
  const fileName = `${tabName}_${format(new Date(), 'yyyy_MM_dd')}.pdf`;

  const blob = await pdf(
    <PDFDocument tabName={tabName} graphs={graphs} textSections={textSections} coverPageData={coverPageData} />,
  ).toBlob();

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};
